import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { on, selectAll, select } from 'helpers/dom'
import { mapWithOptions } from 'helpers/utils'
import { trapFocus, removeTrapFocus } from 'libs/accessibility'

const selectors = {
  CLOSE_SELECTOR: '.js-close-modal-dialog',
  CONTENT_SELECTOR: '.js-content-modal-dialog',
  SWIPER_SLIDE_SELECTOR: 'swiper-slide',
  OPEN_CLASS: 'modal-dialog-opened',
  OVERLAY_CLASS: 'modal-dialog-overlay',
  VALIDATE_FORM_CLASS: 'validate-form',
  TIMEOUT: 200
}

class ModalDialog extends HTMLElement {
  constructor() {
    super()

    this.init()
  }

  allowTouchMove(el) {
    let currentEl = el

    while (currentEl && currentEl !== document.body) {
      if (currentEl.getAttribute('body-scroll-lock-ignore') !== null) {
        return true
      }
      currentEl = currentEl.parentElement
    }

    return false
  }

  init() {
    this.closeBtn = this.querySelectorAll(selectors.CLOSE_SELECTOR)
    this.contentEl = this.querySelector(selectors.CONTENT_SELECTOR)
    this.overlayEl = select(selectors.OVERLAY_CLASS, this)
    this.isSlideOut = this.dataset && this.dataset.type === 'slideout'
    this.scrollTarget = this.getAttribute('data-scroll-target')
    this.target = this.getAttribute('data-target')
    this.enableBodyScrollLock = (this.getAttribute('data-body-scroll-lock') ?? 'true') === 'true'
    this.attachEvents()
  }

  attachEvents() {
    this.closeBtn.forEach((btn) => on('click', this.hide.bind(this), btn))
    on('click', this.hide.bind(this), this)
    this.contentEl && on('click', (e) => e.stopPropagation(), this.contentEl)
    on(
      'keyup',
      (event) => {
        if (event?.key === 'Escape' || event?.keyCode === 27) this.hide()
      },
      window
    )
  }

  show(target, init = false) {
    this.triggerEl = document.activeElement

    init && this.init()
    /** Hide all opened modal before open new modal */

    const allModals = document.querySelectorAll(selectors.OPEN_CLASS)

    allModals.forEach((modal) => modal.hide())

    this.setAttribute('open', '')
    this.classList.add(selectors.OPEN_CLASS)
    globalEmitter.emit(emitterProps.modal.open, true)

    const options = {
      allowTouchMove: this.allowTouchMove
    }

    if (!this.enableBodyScrollLock) {
      document.body.style.overflow = 'hidden'
    } else if (target) {
      disableBodyScroll(target, options)
    } else if (this.scrollTarget) {
      const scrollTargetEl = selectAll(this.scrollTarget, this)

      mapWithOptions(disableBodyScroll, options, [...scrollTargetEl, this.contentEl])
    } else {
      disableBodyScroll(this.contentEl, options)
    }

    setTimeout(() => {
      trapFocus(this.contentEl)
    }, selectors.TIMEOUT)
  }

  hide() {
    /** Refresh validate form when hide modal */
    const allFormsValidate = this.querySelectorAll(selectors.VALIDATE_FORM_CLASS)

    allFormsValidate.forEach((form) => form.refreshValidate())

    this.removeAttribute('open')
    this.classList.remove(selectors.OPEN_CLASS)

    const modalOpeningEls = Array.from(document.querySelectorAll(`.${selectors.OPEN_CLASS}`))

    if (modalOpeningEls.length < 1) {
      clearAllBodyScrollLocks()
      document.body.style.overflow = ''
    }

    globalEmitter.emit(emitterProps.modal.close, this.target)
    removeTrapFocus()
    setTimeout(() => {
      const triggerSwiperSlideEl = this.triggerEl?.closest(selectors.SWIPER_SLIDE_SELECTOR) || null

      this.triggerEl && !triggerSwiperSlideEl && this.triggerEl.focus()
    }, selectors.TIMEOUT)
  }
}

customElements.define('modal-dialog', ModalDialog)
